import { useMemo } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { ROUTES } from 'App/Routes/constants';
import { ID_VALIDATION_ROUTES } from 'commons/constants/idValidationRoutes';
import { useBankAccount, useIdentityValidation } from 'commons/hooks';
import { isJuridicalPerson } from 'commons/utils';
import { getPendingDocNavigationFlags } from 'commons/utils/getKycError';

export type RepresentationArg = {
  representationType: string;
  isPepOrSo: boolean;
};

type RouteArg = boolean | RepresentationArg;

type RouteRule =
  | { route: string; getNextRoute: () => string }
  | { route: string; getNextRoute: (arg: boolean) => string }
  | { route: string; getNextRoute: (arg: RepresentationArg) => string };

const introductoryInfoRoute = `${ROUTES.IDENTITY_VALIDATION}`;
export const isPepOrSoRoute = `${ROUTES.IDENTITY_VALIDATION}${ID_VALIDATION_ROUTES.IS_PEP_OR_SO}`;
const pepOrSoContactRoute = `${ROUTES.IDENTITY_VALIDATION}${ID_VALIDATION_ROUTES.PEP_OR_SO_CONTACT}`;
const representationRoute = `${ROUTES.IDENTITY_VALIDATION}${ID_VALIDATION_ROUTES.REPRESENTATION}`;
const juridicalValidationRoute = `${ROUTES.IDENTITY_VALIDATION}${ID_VALIDATION_ROUTES.JURIDICAL_VALIDATION}`;
const additionalDocumentationRoute = `${ROUTES.IDENTITY_VALIDATION}${ID_VALIDATION_ROUTES.ADDITIONAL_DOCUMENTATION}`;
const proofOfLifeRoute = `${ROUTES.IDENTITY_VALIDATION}${ID_VALIDATION_ROUTES.PROOF_OF_LIFE}`;
const configRoute = `${ROUTES.CONFIG}`;
const landingRoute = `${ROUTES.LANDING}`;
const homeRoute = `${ROUTES.HOME}`;
const idValidationStepsRoute = `${ROUTES.IDENTITY_VALIDATION_STEPS}`;

export function useIdValidationRoutes() {
  const { push } = useHistory();

  const { path: currentRoute } = useRouteMatch();

  const bankAccount = useBankAccount();
  const isJuridical = isJuridicalPerson(bankAccount.data?.fiscalDocument);

  const identityValidation = useIdentityValidation();
  const isInPendingDoc =
    identityValidation.data?.status === 'pending_documentation';

  const isPending = identityValidation.data?.status === 'pending';

  const {
    hasProofOfLifeError,
    hasStatuteError,
    hasUnderageError,
    hasDocumentsMissingFieldsError,
    hasLegalRepresentativeNotFoundInCompanyError,
    hasCopyOfIdFrontError,
    hasCopyOfIdBackError,
  } = getPendingDocNavigationFlags(
    identityValidation.data?.kycDocumentErrors || [],
  );

  const redirectionRules: RouteRule[] = useMemo(() => {
    const proofOfLifeRule: RouteRule = {
      route: proofOfLifeRoute,
      getNextRoute: () => {
        if (isPending) return idValidationStepsRoute;
        return configRoute;
      },
    };

    // Physical person flow
    if (!isJuridical) {
      const getNextRouteByPendingDocErrors = () => {
        if (isInPendingDoc) {
          if (hasStatuteError) return juridicalValidationRoute;
          if (hasLegalRepresentativeNotFoundInCompanyError)
            return additionalDocumentationRoute;
          if (hasProofOfLifeError) return proofOfLifeRoute;
          if (hasUnderageError) return idValidationStepsRoute;
          if (hasDocumentsMissingFieldsError) return proofOfLifeRoute;
          // The proof of life step is where the ID photos are re-captured.
          if (hasCopyOfIdFrontError || hasCopyOfIdBackError)
            return proofOfLifeRoute;
        }
        return introductoryInfoRoute;
      };
      return [
        { route: introductoryInfoRoute, getNextRoute: () => isPepOrSoRoute },
        {
          route: isPepOrSoRoute,
          getNextRoute: (isPepOrSo: boolean) => {
            if (isPepOrSo) return pepOrSoContactRoute;
            return proofOfLifeRoute;
          },
        },
        { route: pepOrSoContactRoute, getNextRoute: () => configRoute },
        proofOfLifeRule,
        { route: configRoute, getNextRoute: getNextRouteByPendingDocErrors },
        { route: landingRoute, getNextRoute: getNextRouteByPendingDocErrors },
        { route: homeRoute, getNextRoute: getNextRouteByPendingDocErrors },
      ];
    }

    // Juridical flow
    const getNextRouteByPendingDocErrors = () => {
      if (isInPendingDoc) {
        if (hasStatuteError) return juridicalValidationRoute;
        if (hasLegalRepresentativeNotFoundInCompanyError)
          return additionalDocumentationRoute;
        if (hasProofOfLifeError) return proofOfLifeRoute;
        if (hasUnderageError) return idValidationStepsRoute;
        if (hasDocumentsMissingFieldsError) return juridicalValidationRoute;
        // The proof of life step is where the ID photos are re-captured.
        if (hasCopyOfIdFrontError || hasCopyOfIdBackError)
          return proofOfLifeRoute;
      }
      return representationRoute;
    };
    return [
      { route: introductoryInfoRoute, getNextRoute: () => representationRoute },
      {
        route: isPepOrSoRoute,
        getNextRoute: (isPepOrSo: boolean) => {
          if (isPepOrSo) return pepOrSoContactRoute;
          return proofOfLifeRoute;
        },
      },
      {
        route: representationRoute,
        getNextRoute: (arg: RepresentationArg) => {
          if (arg.isPepOrSo) return pepOrSoContactRoute;
          if (arg.representationType === 'powerOfAttorney')
            return additionalDocumentationRoute;
          return juridicalValidationRoute;
        },
      },
      {
        route: additionalDocumentationRoute,
        getNextRoute: () => juridicalValidationRoute,
      },
      { route: pepOrSoContactRoute, getNextRoute: () => configRoute },
      {
        route: juridicalValidationRoute,
        getNextRoute: () => {
          if (isInPendingDoc && !hasProofOfLifeError) {
            return configRoute;
          }
          return proofOfLifeRoute;
        },
      },
      proofOfLifeRule,
      { route: configRoute, getNextRoute: getNextRouteByPendingDocErrors },
      { route: landingRoute, getNextRoute: getNextRouteByPendingDocErrors },
      { route: homeRoute, getNextRoute: getNextRouteByPendingDocErrors },
    ];
  }, [
    hasProofOfLifeError,
    hasStatuteError,
    hasUnderageError,
    hasDocumentsMissingFieldsError,
    hasLegalRepresentativeNotFoundInCompanyError,
    hasCopyOfIdFrontError,
    hasCopyOfIdBackError,
    isInPendingDoc,
    isPending,
    isJuridical,
  ]);

  const getNextRoute = useMemo(() => {
    return (routeArg?: RouteArg) => {
      const redirection = redirectionRules.find(
        (rule) => rule.route === currentRoute,
      );
      if (redirection) {
        return (redirection.getNextRoute as (arg?: RouteArg) => string)(
          routeArg,
        );
      }
    };
  }, [redirectionRules, currentRoute]);

  const goToNextRoute = useMemo(() => {
    return (routeArg?: RouteArg) => {
      const nextRoute = getNextRoute(routeArg);
      if (nextRoute) {
        push(nextRoute);
      }
    };
  }, [getNextRoute, push]);

  return { goToNextRoute, getNextRoute };
}
